import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useLlmModels } from "@/hooks/useLlmModels";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";
import { AlertCircle, ChevronDown, ChevronRight, History, Settings } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router";
import DebugInfoJsonViewer from "./run-stage/DebugInfoJsonViewer";
import { ExpandableCodeChip } from "./run-stage/ExpandableCodeChip";
import { ExpandableDiagnosisCodeChip } from "./run-stage/ExpandableDiagnosisCodeChip";
import { getAllDiagnoses, getAllProcedures } from "./run-stage/normaliseCodesForDisplay";
import useEncounterExpectedCodes from "./run-stage/useEncounterExpectedCodes";
import useRegressions from "./run-stage/useRegressions";
import type { Diagnosis, Procedure } from "@/types/classification";
import useRunResultWithStages from "./useRunResultWithStages";
import { PatientEncounterDialog } from "./PatientEncounterDialog";

interface CombinedOutputPageProps {
  runId: string;
}

const CombinedOutputPage = ({ runId }: CombinedOutputPageProps) => {
  const navigate = useNavigate();
  const { data: llmModels } = useLlmModels();
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  const [patientEncounterDialogOpen, setPatientEncounterDialogOpen] = useState(false);

  const { data: run, ...runQueryResult } = useRunResultWithStages({ runId });
  const codesQuery = useEncounterExpectedCodes({ testRunId: run?.id });

  const {
    lastSuccessQuery: { data: lastSuccessfulRun },
    regressionsQuery: { data: regressions },
  } = useRegressions({
    runId: run?.id,
    runEndTime: run?.run_end_time,
  });

  const toggleStage = (stageKey: string) => {
    const newExpanded = new Set(expandedStages);
    if (newExpanded.has(stageKey)) {
      newExpanded.delete(stageKey);
    } else {
      newExpanded.add(stageKey);
    }
    setExpandedStages(newExpanded);
  };

  const isStageExpanded = (stageKey: string) => expandedStages.has(stageKey);

  if (runQueryResult.isLoading || !run) {
    return <div>Loading combined output...</div>;
  }

  if (codesQuery.error) {
    return <div>Error loading combined output: {codesQuery.error.message}</div>;
  }

  const expectedProcedures = codesQuery.data?.test_encounter?.expected_procedures ?? [];
  const expectedDiagnoses = codesQuery.data?.test_encounter?.expected_diagnoses ?? [];

  const procedures = getAllProcedures({
    expectedProcedures: expectedProcedures.map((proc) => ({
      ...proc.opcs4_codes,
      id: proc.opcs4_code_id,
      type: "OPCS-4" as const,
    })),
    runProcedures: run.procedures ?? [],
  });

  const diagnoses = getAllDiagnoses({
    expectedDiagnoses: expectedDiagnoses.map((diag) => ({
      ...diag.icd10_codes,
      id: diag.icd10_code_id,
      isOptional: !!diag.is_optional,
      type: "ICD-10" as const,
    })),
    runDiagnoses: run.diagnoses ?? [],
  });

  if (!run.combineEndoHistoStageResult) {
    return null;
  }

  return (
    <>
      <PatientEncounterDialog
        open={patientEncounterDialogOpen}
        onClose={() => setPatientEncounterDialogOpen(false)}
        patientEncounterId={codesQuery.data?.patient_encounter_id || ""}
      />
      <div className="h-[calc(100vh-4rem)] flex">
        {/* Left column - Summary info */}
        <div className="w-full max-w-md border-r h-full overflow-y-auto p-2">
          <Card className="rounded-md border bg-muted/30 mb-6">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <span className="inline-block px-2 py-1 rounded-full text-sm bg-green-100 text-green-800">
                    Combined Output
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPatientEncounterDialogOpen(true)}
                  title="View Patient Encounter Expected Codes"
                  aria-label="View Patient Encounter Expected Codes"
                  disabled={!codesQuery.data?.patient_encounter_id}
                >
                  <Settings className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm">
                <div>
                  <span className="font-medium">Status:</span>{" "}
                  <span
                    className={cn(
                      "px-2 py-1 rounded-full text-xs",
                      run.run_end_time ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800",
                    )}
                  >
                    {run.run_end_time ? "Completed" : "Running"}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Final Diagnoses:</span> {diagnoses.length}
                </div>
                <div>
                  <span className="font-medium">Final Procedures:</span> {procedures.length}
                </div>
                <div>
                  <span className="font-medium">HRG Assignment:</span>{" "}
                  {run.testEncounterResult?.test_run_hrg?.length || 0}
                </div>
                {run.run_start_time && run.run_end_time && (
                  <div>
                    <span className="font-medium">Duration:</span>{" "}
                    {dayjs(run.run_end_time).diff(dayjs(run.run_start_time), "second")}s
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="flex-1 h-full overflow-auto p-6 space-y-6 max-w-full">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold tracking-tight flex items-center gap-4">
                Combined Output
                {run.run_end_time && (
                  <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                    {dayjs(run.run_start_time).tz().format("DD/MM HH:mm")}
                  </span>
                )}
                {regressions && regressions.length > 0 && (
                  <span className="flex items-center gap-1 text-base font-normal bg-red-100 text-red-800 px-3 py-1 rounded-full ml-2">
                    <AlertCircle className="h-4 w-4 mr-1 text-red-600" />
                    Regression
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      title="Go to last successful run"
                      aria-label="Go to last successful run"
                      disabled={!lastSuccessfulRun}
                      onClick={() => {
                        if (lastSuccessfulRun) {
                          navigate(`/test-data/runs/${lastSuccessfulRun.id}`);
                        }
                      }}
                    >
                      <History className="h-5 w-5" />
                    </Button>
                  </span>
                )}
                <span className="text-base font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full ml-2">
                  {llmModels
                    ? llmModels.find((model) => model.modelId === run.llm_model_id)?.name || "Unknown"
                    : "Loading model..."}
                </span>
              </h1>
              <p className="text-muted-foreground">Combined results from all stages for run {run.id}</p>
              {run.error_message && (
                <div className="text-red-500 mt-2">
                  <p className="font-medium">There was a top level error preventing classification:</p>
                  <pre className="text-sm bg-muted p-2 rounded mt-1 whitespace-pre-wrap overflow-x-auto max-w-full break-words">
                    {run.error_message}
                  </pre>
                </div>
              )}
            </div>
          </div>

          {run.testEncounterResult?.review_summary && (
            <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
              <h2 className="text-lg font-medium mb-2">
                Review Summary: {run.testEncounterResult.should_flag_for_review ? "🚩🚩" : "✅✅"}
              </h2>
              <div className="text-sm text-muted-foreground whitespace-pre-wrap break-words">
                {run.testEncounterResult.review_summary}
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Final Codes Output - Prominently displayed */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Final Codes Output</h2>

              {/* Procedures Section */}
              <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
                <h3 className="text-lg font-medium mb-2">Procedures</h3>
                <div className="flex flex-col gap-2">
                  {procedures.map((proc) => (
                    <ExpandableCodeChip
                      key={proc.id || proc.code}
                      codeId={proc.id}
                      code={proc.code}
                      description={proc.description}
                      isExpected={proc.isExpected}
                      isPresent={proc.isPresent}
                      isPositionCorrect={proc.isPositionCorrect}
                    />
                  ))}
                  {procedures.length === 0 && <div className="text-muted-foreground">No procedures found</div>}
                </div>
              </div>

              {/* Diagnoses Section */}
              <div className="rounded-md border p-4 space-y-4 w-full max-w-full">
                <h3 className="text-lg font-medium mb-2">Diagnoses</h3>
                <div className="flex flex-col gap-2">
                  {diagnoses.map((diagnosis, index) => (
                    <ExpandableDiagnosisCodeChip
                      key={diagnosis.id || diagnosis.code}
                      codeId={diagnosis.id}
                      code={diagnosis.code}
                      description={diagnosis.description}
                      isExpected={diagnosis.isExpected}
                      isPresent={diagnosis.isPresent}
                      isOptional={diagnosis.isOptional ?? false}
                      isPositionCorrect={diagnosis.isPositionCorrect}
                      isPrimary={index === 0}
                    />
                  ))}
                  {diagnoses.length === 0 && <div className="text-muted-foreground">No diagnoses found</div>}
                </div>
              </div>
            </div>

            {/* Stage Results - Expandable Cards */}
            <div className="space-y-4">
              <h2 className="text-2xl font-semibold">Stage Results</h2>

              {/* Combine Endo+Histo Stage */}
              {run.combineEndoHistoStageResult && (
                <Collapsible
                  open={isStageExpanded("combineEndoHisto")}
                  onOpenChange={() => toggleStage("combineEndoHisto")}
                >
                  <Card>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                              Combine Endo+Histo
                            </span>
                            <span className="text-sm text-muted-foreground">
                              ({run.combineEndoHistoStageResult.result?.diagnoses?.length || 0} diagnoses,{" "}
                              {run.combineEndoHistoStageResult.result?.procedures?.length || 0} procedures)
                            </span>
                          </div>
                          {isStageExpanded("combineEndoHisto") ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {run.combineEndoHistoStageResult.result && (
                          <div className="space-y-4">
                            {/* Diagnoses */}
                            <div className="rounded-md border p-4 space-y-4">
                              <h4 className="text-md font-medium">Diagnoses</h4>
                              <div className="flex flex-col gap-2">
                                {run.combineEndoHistoStageResult.result.diagnoses.map((diagnosis: Diagnosis) => (
                                  <div
                                    key={diagnosis.id || diagnosis.code}
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium max-w-full",
                                      "bg-gray-200 text-gray-800 dark:bg-gray-900 dark:text-gray-100",
                                    )}
                                  >
                                    <span className="font-semibold shrink-0">{diagnosis.code}</span>
                                    <span className="mx-1 shrink-0">•</span>
                                    <span className="truncate">{diagnosis.description}</span>
                                  </div>
                                ))}
                                {run.combineEndoHistoStageResult.result.diagnoses.length === 0 && (
                                  <div className="text-muted-foreground">No diagnoses found</div>
                                )}
                              </div>
                            </div>

                            {/* Procedures */}
                            <div className="rounded-md border p-4 space-y-4">
                              <h4 className="text-md font-medium">Procedures</h4>
                              <div className="flex flex-col gap-2">
                                {run.combineEndoHistoStageResult.result.procedures.map((procedure: Procedure) => (
                                  <div
                                    key={procedure.id || procedure.code}
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium max-w-full",
                                      "bg-gray-200 text-gray-800 dark:bg-gray-900 dark:text-gray-100",
                                    )}
                                  >
                                    <span className="font-semibold shrink-0">{procedure.code}</span>
                                    <span className="mx-1 shrink-0">•</span>
                                    <span className="truncate">{procedure.description}</span>
                                  </div>
                                ))}
                                {run.combineEndoHistoStageResult.result.procedures.length === 0 && (
                                  <div className="text-muted-foreground">No procedures found</div>
                                )}
                              </div>
                            </div>

                            {/* Error Message */}
                            {run.combineEndoHistoStageResult.result.errorMessage && (
                              <div className="rounded-md border-2 border-red-500 bg-red-50 p-4">
                                <h4 className="text-md font-medium text-red-800 mb-2">Error</h4>
                                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">
                                  {run.combineEndoHistoStageResult.result.errorMessage}
                                </div>
                              </div>
                            )}

                            {/* Debug Info */}
                            {run.combineEndoHistoStageResult.result.debugInfo && (
                              <div className="rounded-md border p-4">
                                <h4 className="text-md font-medium mb-2">Debug Info</h4>
                                <DebugInfoJsonViewer debugInfo={run.combineEndoHistoStageResult.result.debugInfo} />
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}

              {/* Consolidate Diagnoses Stage */}
              {run.consolidateDiagnosesStageResult && (
                <Collapsible
                  open={isStageExpanded("consolidateDiagnoses")}
                  onOpenChange={() => toggleStage("consolidateDiagnoses")}
                >
                  <Card>
                    <CollapsibleTrigger asChild>
                      <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="inline-block px-2 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                              Consolidated Diagnoses
                            </span>
                            <span className="text-sm text-muted-foreground">
                              ({run.consolidateDiagnosesStageResult.result?.diagnoses?.length || 0} diagnoses)
                            </span>
                          </div>
                          {isStageExpanded("consolidateDiagnoses") ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="pt-0">
                        {run.consolidateDiagnosesStageResult.result && (
                          <div className="space-y-4">
                            {/* Diagnoses */}
                            <div className="rounded-md border p-4 space-y-4">
                              <h4 className="text-md font-medium">Diagnoses</h4>
                              <div className="flex flex-col gap-2">
                                {run.consolidateDiagnosesStageResult.result.diagnoses.map((diagnosis: Diagnosis) => (
                                  <div
                                    key={diagnosis.id || diagnosis.code}
                                    className={cn(
                                      "inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium max-w-full",
                                      "bg-gray-200 text-gray-800 dark:bg-gray-900 dark:text-gray-100",
                                    )}
                                  >
                                    <span className="font-semibold shrink-0">{diagnosis.code}</span>
                                    <span className="mx-1 shrink-0">•</span>
                                    <span className="truncate">{diagnosis.description}</span>
                                  </div>
                                ))}
                                {run.consolidateDiagnosesStageResult.result.diagnoses.length === 0 && (
                                  <div className="text-muted-foreground">No diagnoses found</div>
                                )}
                              </div>
                            </div>

                            {/* Error Message */}
                            {run.consolidateDiagnosesStageResult.result.errorMessage && (
                              <div className="rounded-md border-2 border-red-500 bg-red-50 p-4">
                                <h4 className="text-md font-medium text-red-800 mb-2">Error</h4>
                                <div className="text-sm text-red-600 whitespace-pre-wrap break-words">
                                  {run.consolidateDiagnosesStageResult.result.errorMessage}
                                </div>
                              </div>
                            )}

                            {/* Debug Info */}
                            {run.consolidateDiagnosesStageResult.result.debugInfo && (
                              <div className="rounded-md border p-4">
                                <h4 className="text-md font-medium mb-2">Debug Info</h4>
                                <DebugInfoJsonViewer debugInfo={run.consolidateDiagnosesStageResult.result.debugInfo} />
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CombinedOutputPage;
